import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { Icon, IconInput } from '../icon/icon';

/**
 * A sidenav-footer link out to another app on a different origin — used by the
 * partner-area and admin shells to jump back to the main Halio (`users`) app.
 *
 * A plain cross-origin anchor rather than a `routerLink`: the target is a
 * separate app on another origin, so this leaves the SPA entirely. It opens in
 * a new tab (the current surface stays put behind it), and the accessible name
 * says so.
 *
 * Presentational only — the host app supplies the `href` (from its environment)
 * and `label`. Drop it into the sidenav footer slot:
 *
 * ```html
 * <ui-back-to-app uiSidenavFooter [href]="usersAppUrl" label="Back to Halio" />
 * ```
 *
 * Styled to match the sidenav footer controls, and collapses to just the icon
 * in the icon rail via the `data-rail` attribute the sidenav sets on the footer
 * slot wrapper.
 */
@Component({
  selector: 'ui-back-to-app',
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }

    .trigger {
      display: flex;
      align-items: center;
      gap: var(--space-200);
      width: 100%;
      padding: var(--space-200);
      border: 1px solid var(--border-default-default);
      border-radius: var(--radius-200);
      background: var(--background-default-default);
      color: var(--text-default-default);
      text-align: left;
      text-decoration: none;

      &:hover {
        background: var(--background-default-default-hover);
      }
    }

    .trigger__mark {
      flex: none;
      color: var(--icon-default-secondary);
    }

    .trigger__label {
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Collapsed icon rail — show just the mark, drop the label. */
    :host-context([data-rail='true']) {
      .trigger {
        justify-content: center;
        padding: var(--space-200) 0;
      }

      .trigger__label {
        display: none;
      }
    }
  `,
  template: `
    <a
      class="trigger"
      [href]="href()"
      target="_blank"
      rel="noopener"
      [attr.aria-label]="label() + ' (opens in a new tab)'"
    >
      <ui-icon [name]="icon()" class="trigger__mark" [strokeWidth]="1.5" />
      <span class="trigger__label body-sm strong">{{ label() }}</span>
    </a>
  `,
})
export class BackToApp {
  /** Absolute URL of the app to open (a different origin). */
  readonly href = input.required<string>();
  /** Visible text and the base of the accessible name. */
  readonly label = input.required<string>();
  /** Leading icon; defaults to the external-link mark since it opens a new tab. */
  readonly icon = input<IconInput>('external-link');
}
